import { useState, useEffect } from 'react';
import { CreditCard as Edit2, Trash2, Users, Package, FileText, TrendingUp } from 'lucide-react';
import type { CampaignWithComputed } from '../types/campaign';
import type { UserRole } from '../types/user';
import { canEditCampaign, canDeleteCampaign } from '../lib/permissions';
import { campaignInfluencerService } from '../services/campaignInfluencerService';
import { campaignServiceService } from '../services/campaignServiceService';
import { invoiceService } from '../services/invoiceService';
import { calculateInfluencerCommission } from '../hooks/useCampaignCalculations';
import { formatEuro } from '../utils/currency';
import { getCampaignStatusBadgeClass } from '../utils/statusColors';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { log } from '../lib/logger';
import { ExpandToggle } from './ui/ListRows';

interface MultiInfluencerTableViewProps {
  campaigns: CampaignWithComputed[];
  onEdit: (campaign: CampaignWithComputed) => void;
  onDelete: (id: string) => void;
  userRole: UserRole | null;
  currentUserId?: string;
}

interface CampaignStats {
  influencersCount: number;
  servicesCount: number;
  clientInvoicesCount: number;
  influencerInvoicesCount: number;
  totalInfluencerCosts: number;
  totalServiceCosts: number;
  totalInfluencerCommissions: number;
  budgetMargin: number;
  isCommissionOnlyMode: boolean;
  influencers?: any[];
  services?: any[];
  clientInvoices?: any[];
  influencerInvoices?: any[];
  nextUnpaidInvoice?: any;
}

export function MultiInfluencerTableView({ campaigns, onEdit, onDelete, userRole, currentUserId }: MultiInfluencerTableViewProps) {
  const { t } = useLanguage();
  const { showConfirm } = useModal();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [campaignStats, setCampaignStats] = useState<Map<string, CampaignStats>>(new Map());
  const [loadingStats, setLoadingStats] = useState<Set<string>>(new Set());

  const toggleRow = async (campaignId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(campaignId)) {
      newExpanded.delete(campaignId);
    } else {
      newExpanded.add(campaignId);
      // Load detailed stats if not already loaded
      if (!campaignStats.has(campaignId)) {
        await loadCampaignStats(campaignId);
      }
    }
    setExpandedRows(newExpanded);
  };

  const loadCampaignStats = async (campaignId: string) => {
    setLoadingStats(prev => new Set(prev).add(campaignId));
    try {
      const [influencers, services, invoices] = await Promise.all([
        campaignInfluencerService.getByCampaign(campaignId),
        campaignServiceService.getByCampaign(campaignId),
        invoiceService.getByCampaign(campaignId),
      ]);

      const clientInvoices = invoices.filter(inv => inv.type === 'Client');
      const influencerInvoices = invoices.filter(inv => inv.type === 'Influencer');

      const totalInfluencerCosts = influencers.reduce((sum, inf) => sum + (inf.agreed_fee || 0), 0);
      const totalServiceCosts = services.reduce((sum, svc) => sum + (svc.agreed_fee || 0), 0);
      const totalInfluencerCommissions = influencers.reduce((sum, inf) => {
        const commission = calculateInfluencerCommission(
          inf.agreed_fee || 0,
          inf.commission_pct ?? 0,
          inf.commission_flat_fee ?? 0,
          inf.commission_type ?? 'percentage'
        );
        return sum + commission;
      }, 0);

      const campaign = campaigns.find(c => c.id === campaignId);
      const isCommissionOnlyMode = (campaign?.campaign_budget || 0) === 0 && influencers.length > 0;
      const budgetMargin = isCommissionOnlyMode
        ? totalInfluencerCommissions
        : ((campaign?.campaign_budget || 0) - totalInfluencerCosts - totalServiceCosts) + totalInfluencerCommissions;

      // Find next unpaid invoice (prioritize overdue, then by due date)
      const unpaidInvoices = invoices.filter(inv => !['Paid', 'Canceled'].includes(inv.status));
      const nextUnpaidInvoice = unpaidInvoices.sort((a, b) => {
        // Sort by: overdue first, then by due date
        if (a.is_overdue && !b.is_overdue) return -1;
        if (!a.is_overdue && b.is_overdue) return 1;
        if (a.due_date && b.due_date) return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        if (a.due_date && !b.due_date) return -1;
        if (!a.due_date && b.due_date) return 1;
        return (a.display_order || 0) - (b.display_order || 0);
      })[0];

      setCampaignStats(prev => new Map(prev).set(campaignId, {
        influencersCount: influencers.length,
        servicesCount: services.length,
        clientInvoicesCount: clientInvoices.length,
        influencerInvoicesCount: influencerInvoices.length,
        totalInfluencerCosts,
        totalServiceCosts,
        totalInfluencerCommissions,
        budgetMargin,
        isCommissionOnlyMode,
        influencers,
        services,
        clientInvoices,
        influencerInvoices,
        nextUnpaidInvoice,
      }));
    } catch (error) {
      log.error('Failed to load campaign stats:', error);
    } finally {
      setLoadingStats(prev => {
        const next = new Set(prev);
        next.delete(campaignId);
        return next;
      });
    }
  };

  // Preload stats for all campaigns
  useEffect(() => {
    const loadAllStats = async () => {
      for (const campaign of campaigns) {
        if (!campaignStats.has(campaign.id)) {
          await loadCampaignStats(campaign.id);
        }
      }
    };
    loadAllStats();
  }, [campaigns]);

  const getBudgetMarginColor = (margin: number, budget: number, isCommissionOnlyMode: boolean) => {
    if (isCommissionOnlyMode) return 'text-green-700 font-semibold';
    if (margin < 0) return 'text-red-700 font-bold';
    if (budget === 0) return 'text-gray-500';
    const marginPct = (margin / budget) * 100;
    if (marginPct < 10) return 'text-yellow-700 font-semibold';
    return 'text-green-700 font-semibold';
  };

  const getInvoiceStatusSummary = (invoices: any[]) => {
    const statusCounts: Record<string, number> = {};
    invoices.forEach(inv => {
      statusCounts[inv.status] = (statusCounts[inv.status] || 0) + 1;
    });
    return Object.entries(statusCounts)
      .map(([status, count]) => `${count} ${status}`)
      .join(', ');
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Code</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign Ref</th>
            <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
            <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <Users className="w-4 h-4 mx-auto" title="Influencers" />
            </th>
            <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <Package className="w-4 h-4 mx-auto" title="Services" />
            </th>
            <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Client Inv.</th>
            <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Influencer Inv.</th>
            <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              <span title="(Budget - Costs) + Influencer Commissions">Margin</span>
            </th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {campaigns.map((campaign, index) => {
            const stats = campaignStats.get(campaign.id);
            const isExpanded = expandedRows.has(campaign.id);
            const isLoading = loadingStats.has(campaign.id);
            const isEven = index % 2 === 0;
            const rowBgClass = isEven ? 'bg-white' : 'bg-gray-50';

            return (
              <>
                <tr key={campaign.id} className={`group ${isExpanded ? 'bg-gray-50' : rowBgClass} hover:bg-gray-50 transition-colors duration-150 cursor-pointer`} onClick={() => toggleRow(campaign.id)}>
                  <td className="px-2 py-2">
                    <ExpandToggle open={isExpanded} />
                  </td>
                  <td className="px-2 py-2 text-sm text-gray-900">{campaign.agent_name || '-'}</td>
                  <td className="px-2 py-2 text-sm" onClick={(e) => e.stopPropagation()}>
                    {canEditCampaign(userRole, campaign, currentUserId) ? (
                      <button
                        onClick={() => onEdit(campaign)}
                        className="font-mono text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors"
                        title="Edit campaign"
                      >
                        {campaign.work_code}
                      </button>
                    ) : (
                      <span className="font-mono text-gray-600 font-medium">
                        {campaign.work_code}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-sm text-gray-900">{campaign.brand || '-'}</td>
                  <td className="px-2 py-2 text-sm text-gray-500">{campaign.campaign_ref || '-'}</td>
                  <td className="px-2 py-2 text-sm text-right text-gray-900 font-medium">
                    {formatEuro(campaign.campaign_budget || 0)}
                  </td>
                  <td className="px-2 py-2 text-sm text-center">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {stats?.influencersCount ?? '-'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-sm text-center">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {stats?.servicesCount ?? '-'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-sm text-center">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {stats?.clientInvoicesCount ?? '-'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-sm text-center">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {stats?.influencerInvoicesCount ?? '-'}
                    </span>
                  </td>
                  <td className={`px-2 py-2 text-sm text-right ${getBudgetMarginColor(stats?.budgetMargin ?? 0, campaign.campaign_budget || 0, stats?.isCommissionOnlyMode ?? false)}`}>
                    <div className="flex flex-col items-end gap-0.5">
                      {stats ? formatEuro(stats.budgetMargin) : '-'}
                      {stats?.isCommissionOnlyMode && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                          {t('financial.commissionOnly')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getCampaignStatusBadgeClass(campaign.campaign_status || 'Consultation', 'solid')}`}>
                        {campaign.campaign_status || 'Consultation'}
                      </span>
                      {campaign.campaign_status === 'Billing' && stats?.nextUnpaidInvoice && (
                        <div className="text-xs text-gray-600 flex items-center gap-1">
                          <span className={`font-medium ${stats.nextUnpaidInvoice.is_overdue ? 'text-red-600' : 'text-blue-600'}`}>
                            Next: {stats.nextUnpaidInvoice.type === 'Client' ? 'C' : 'I'}-{stats.nextUnpaidInvoice.number || '?'}
                          </span>
                          {stats.nextUnpaidInvoice.due_date && (
                            <span className="text-gray-500">
                              ({new Date(stats.nextUnpaidInvoice.due_date).toLocaleDateString()})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right text-sm" onClick={(e) => e.stopPropagation()}>
                    {canEditCampaign(userRole, campaign, currentUserId) && (
                      <button
                        onClick={() => onEdit(campaign)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {canDeleteCampaign(userRole, campaign, currentUserId) && (
                      <button
                        onClick={async () => {
                          const confirmed = await showConfirm({
                            title: 'Delete Campaign?',
                            message: `Are you sure you want to delete campaign ${campaign.campaign_ref}? This action cannot be undone.`,
                            confirmText: 'Delete Campaign',
                            cancelText: 'Cancel',
                            variant: 'danger',
                          });
                          if (confirmed) {
                            onDelete(campaign.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>

                {isExpanded && stats && (
                  <tr key={`${campaign.id}-details`} className={rowBgClass}>
                    <td colSpan={13} className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100">
                      {isLoading ? (
                        <div className="text-center py-3 text-gray-500 text-sm">Loading details...</div>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Influencers Section */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              Influencers ({stats.influencersCount})
                            </h4>
                            {stats.influencers && stats.influencers.length > 0 ? (
                              <div className="space-y-1.5">
                                {stats.influencers.map((inf: any) => (
                                  <div key={inf.id} className="bg-white p-2 rounded border border-gray-200 shadow-sm">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="font-medium text-gray-900">{inf.influencer_name}</div>
                                        <div className="text-xs text-gray-500">
                                          Fee: {formatEuro(inf.agreed_fee || 0)} | Commission: {inf.commission_type === 'flat_fee' ? formatEuro(inf.commission_flat_fee || 0) : `${((inf.commission_pct ?? 0) * 100).toFixed(0)}%`} ({formatEuro(calculateInfluencerCommission(inf.agreed_fee || 0, inf.commission_pct ?? 0, inf.commission_flat_fee ?? 0, inf.commission_type ?? 'percentage'))})
                                        </div>
                                      </div>
                                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                        inf.status === 'Paid' ? 'bg-green-100 text-green-800' :
                                        inf.status === 'Published' ? 'bg-teal-100 text-teal-800' :
                                        inf.status === 'In Production' ? 'bg-blue-100 text-blue-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {inf.status}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No influencers</p>
                            )}
                          </div>

                          {/* Services Section */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <Package className="w-4 h-4" />
                              Services ({stats.servicesCount})
                            </h4>
                            {stats.services && stats.services.length > 0 ? (
                              <div className="space-y-1.5">
                                {stats.services.map((svc: any) => (
                                  <div key={svc.id} className="bg-white p-2 rounded border border-gray-200 shadow-sm">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="font-medium text-gray-900">{svc.service_provider_name}</div>
                                        <div className="text-xs text-gray-500">
                                          {svc.service_type} | Fee: {formatEuro(svc.agreed_fee || 0)}
                                        </div>
                                      </div>
                                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${getCampaignStatusBadgeClass(svc.status, 'solid')}`}>
                                        {svc.status}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No services</p>
                            )}
                          </div>

                          {/* Client Invoices Section */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Client Invoices ({stats.clientInvoicesCount})
                            </h4>
                            {stats.clientInvoices && stats.clientInvoices.length > 0 ? (
                              <div className="space-y-1.5">
                                {stats.clientInvoices.map((inv: any) => {
                                  const isNextToPay = stats.nextUnpaidInvoice?.id === inv.id;
                                  return (
                                    <div key={inv.id} className={`bg-white p-2 rounded shadow-sm ${
                                      isNextToPay ? 'border-2 border-blue-500 ring-2 ring-blue-100' : 'border border-gray-200'
                                    }`}>
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <div className="font-medium text-gray-900">{inv.number || 'No #'}</div>
                                            {isNextToPay && (
                                              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                                NEXT TO PAY
                                              </span>
                                            )}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {formatEuro(inv.total)} | Due: {inv.due_date || '-'}
                                            {inv.total_paid > 0 && inv.status !== 'Paid' && (
                                              <span className="ml-1 text-orange-600">
                                                (Paid: {formatEuro(inv.total_paid)})
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                          inv.status === 'Paid' ? 'bg-green-100 text-green-800' :
                                          inv.status === 'Issued' ? 'bg-blue-100 text-blue-800' :
                                          inv.status === 'Partially Paid' ? 'bg-orange-100 text-orange-800' :
                                          inv.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {inv.status}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No client invoices</p>
                            )}
                          </div>

                          {/* Influencer Invoices Section */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Influencer Invoices ({stats.influencerInvoicesCount})
                            </h4>
                            {stats.influencerInvoices && stats.influencerInvoices.length > 0 ? (
                              <div className="space-y-1.5">
                                {stats.influencerInvoices.map((inv: any) => {
                                  const isNextToPay = stats.nextUnpaidInvoice?.id === inv.id;
                                  return (
                                    <div key={inv.id} className={`bg-white p-2 rounded shadow-sm ${
                                      isNextToPay ? 'border-2 border-blue-500 ring-2 ring-blue-100' : 'border border-gray-200'
                                    }`}>
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <div className="font-medium text-gray-900">{inv.number || 'No #'}</div>
                                            {isNextToPay && (
                                              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                                NEXT TO PAY
                                              </span>
                                            )}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {formatEuro(inv.total)}
                                            {inv.due_date && ` | Due: ${inv.due_date}`}
                                            {inv.total_paid > 0 && inv.status !== 'Paid' && (
                                              <span className="ml-1 text-orange-600">
                                                (Paid: {formatEuro(inv.total_paid)})
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                          inv.status === 'Paid' ? 'bg-green-100 text-green-800' :
                                          inv.status === 'Issued' ? 'bg-blue-100 text-blue-800' :
                                          inv.status === 'Partially Paid' ? 'bg-orange-100 text-orange-800' :
                                          inv.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {inv.status}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No influencer invoices</p>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>

      {campaigns.length === 0 && (
        <div className="text-center py-12 bg-gray-50">
          <p className="text-gray-600">No Marketing de Influência campaigns found</p>
        </div>
      )}
    </div>
  );
}
